import React, { useState, useEffect } from 'react';
import { QuoteState, User, LineItem, BusinessInfo, Country, Contact } from '../types.ts';
import { QuoteEditor } from '../components/QuoteEditor.tsx';
import { QuotePreview } from '../components/QuotePreview.tsx';
import { QuoteChatAssistant } from '../components/QuoteChatAssistant.tsx';
import { QuoteFinalizeModal } from '../components/QuoteFinalizeModal.tsx';
import { QuoteManager } from '../components/QuoteManager.tsx';
import { numberToWords, addBusinessDays } from '../utils.ts';
import { getNextQuoteNumber, finalizeAndSaveQuote } from '../services/financeService.ts';
import { apiService } from '../services/apiService.ts';
import { FileText, Bot, CheckCircle2, History, Loader2 } from 'lucide-react';

interface FinanceModuleProps {
  currentUser: User;
  activeCountry: Country;
}

const BUSINESS_PROFILES: Record<string, BusinessInfo> = {
  'UGANDA': {
    name: 'SHIV INTERNATIONAL LTD',
    address: 'Unit No. 18 & 19, Tirupati Business Park,\nKyebando, P.O. Box 35435,\nKampala,Uganda',
    phone: '+256 759407792',
    email: 'shiv@shivgroup.co.ug',
    logoUrl: '/shiv.jpg', 
    tin: '1000267773',
    vrn: '',
    bankDetails: 'A/C Name: Shiv International Ltd\nUGX: 01170010436042 | USD: 02170015928696\nBank Name: DFCU Bank\nBranch: Lugogo Branch, Kampala, Uganda\nSwift: DFCUUGKA'
  },
  'TANZANIA': {
    name: 'SHIV INTERNATIONAL LTD',
    address: '4th Floor, Hekima Point Building,\nCorner of Mindu Street/Malik Road,\nP. O. Box 38268, Dar es Salaam, Tanzania',
    phone: '+255 655 036 568',
    email: 'shiv@shivgroup.co.tz',
    logoUrl: '/shiv.jpg', 
    tin: '104-017-568',
    vrn: '10-018306-C',
    bankDetails: 'A/C NAME: SHIV INTERNATIONAL LTD\nBANK: CRDB BANK (T) LTD | BRANCH: HOLLAND HOUSE,DAR-ES-SALAAM | TSHS A/C NO.: 0150237399200 | USD A/C NO.: 0250237399200 | SWIFT: CORUTZTZ\nBANK: NBC BANK | BRANCH: CORPORATE BRANCH,DAR-ES-SALAAM | TSHS A/C NO.: 011103042660 | USD A/C NO.: 011105020620 | SWIFT: NLCBTZTZ\nBANK: EXIM BANK (TANZANIA) LTD | BRANCH: TOWER BRANCH,DAR-ES-SALAAM | TSHS A/C NO.: 0010058404 | USD A/C NO.: 0010058403 | SWIFT: EXTNTZTZ'
  },
  'KENYA': {
    name: 'KAILASA INTERNATIONAL LTD',
    address: 'Mpaka House, Nairobi, Kenya',
    phone: '+254 784 524524',
    email: 'kailasa@kailasa.co.ke',
    logoUrl: '/kailasa.jpg', 
    tin: 'P052299217G',
    vrn: '',
    bankDetails: 'A/C Name: KAILASA INTERNATIONAL LIMITED\nBank: Standard Chartered Bank\nBranch: Chiromo\nKES A/c No: 0108023456700\nUSD A/c No: 0108023456701\nSwift: SCBLKENA'
  }
};

const FinanceModule: React.FC<FinanceModuleProps> = ({ currentUser, activeCountry }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isFinalizeOpen, setIsFinalizeOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [crmContacts, setCrmContacts] = useState<Contact[]>([]);

  useEffect(() => {
    apiService.getContacts().then(contacts => setCrmContacts(contacts));
  }, []);

  const createDefaultItems = (country: Country): LineItem[] => {
    const defaultUnit = country === Country.TANZANIA ? 'Drums' : 'M.T';
    return [
      { id: crypto.randomUUID(), name: '', description: '(Packed in steel drums)', quantity: 0, price: 0, unit: defaultUnit, drumWeight: 190 },
      { id: crypto.randomUUID(), name: '', description: '(Packed in steel drums)', quantity: 0, price: 0, unit: defaultUnit, drumWeight: 190 }
    ];
  };

  const [quote, setQuote] = useState<QuoteState>(() => {
    const initialCurrency = activeCountry === Country.TANZANIA ? 'TZS' : activeCountry === Country.UGANDA ? 'UGX' : 'KES';
    const now = new Date();
    return {
        business: BUSINESS_PROFILES[activeCountry] || BUSINESS_PROFILES['TANZANIA'],
        client: { name: '', email: '', phone: '', address: '' },
        details: {
            number: 'FETCHING...',
            date: now.toISOString().split('T')[0],
            priceValidity: addBusinessDays(now, 3).toISOString().split('T')[0],
            currency: initialCurrency,
            taxRate: activeCountry === Country.KENYA ? 16 : 18, 
            amountInWords: '',
            paymentTerms: '100% ADVANCE PAYMENT',
            deliveryTerms: "BUYER SHOULD ARRANGE LOADING OF GOODS FROM SELLER'S WAREHOUSE AFTER PAYMENT IN RECEIVED IN SELLER'S BANK ACCOUNT",
            incoterms: 'EX-WAREHOUSE',
            notes: "Invoice/EFD will be issued only in the name of the depositor as shown in the seller's bank statement"
        },
        items: createDefaultItems(activeCountry)
    };
  });

  // Effect to sync persistent document number
  useEffect(() => {
    const syncNumber = async () => {
      setIsSyncing(true);
      try {
        const nextNum = await getNextQuoteNumber(activeCountry);
        setQuote(prev => ({
            ...prev,
            business: BUSINESS_PROFILES[activeCountry] || BUSINESS_PROFILES['TANZANIA'],
            details: {
                ...prev.details,
                number: nextNum,
                currency: activeCountry === Country.TANZANIA ? 'TZS' : activeCountry === Country.UGANDA ? 'UGX' : 'KES',
                taxRate: activeCountry === Country.KENYA ? 16 : 18
            },
            items: createDefaultItems(activeCountry)
        }));
      } catch (e) {
        console.error("Failed to sync proforma sequence", e);
      } finally {
        setIsSyncing(false);
      }
    };
    syncNumber();
  }, [activeCountry]);

  useEffect(() => {
    const activeItems = quote.items.filter(item => item.name && item.name.trim() !== '');
    const isKenya = activeCountry === Country.KENYA;
    
    let subtotal = 0;
    if (isKenya) {
      subtotal = activeItems.reduce((acc, item) => {
        const effectiveRate = Number(item.price || 0) * (item.drumWeight || 1);
        return acc + (Number(item.quantity || 0) * effectiveRate);
      }, 0);
      const taxRateDec = Number(quote.details.taxRate || 0) / 100;
      subtotal = subtotal / (1 + taxRateDec);
    } else {
      subtotal = activeItems.reduce((acc, item) => acc + (Number(item.quantity || 0) * Number(item.price || 0)), 0);
    }

    const taxAmount = subtotal * (quote.details.taxRate / 100);
    const total = isKenya ? (subtotal * (1 + (quote.details.taxRate / 100))) : (subtotal + taxAmount);
    const words = numberToWords(total, quote.details.currency);
    
    if (words !== quote.details.amountInWords) {
      setQuote(prev => ({
        ...prev,
        details: { ...prev.details, amountInWords: words }
      }));
    }
  }, [quote.items, quote.details.taxRate, quote.details.currency, activeCountry]);

  const handleManualSave = async (alias: string) => {
    try {
        await finalizeAndSaveQuote(quote, activeCountry, alias);
        alert(`Registry Updated: Proforma ${quote.details.number} archived.`);
        // Re-sync number for next potential document
        const nextNum = await getNextQuoteNumber(activeCountry);
        setQuote(prev => ({ ...prev, details: { ...prev.details, number: nextNum } }));
        setIsFinalizeOpen(false);
    } catch (e) {
        alert("Failed to save to database. Verify connectivity.");
    }
  };

  return (
    <div className="flex h-full overflow-hidden bg-slate-50">
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 shrink-0 z-20 no-print">
          <div className="flex items-center gap-6">
             <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
                    <FileText size={20} />
                </div>
                <div>
                    <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight">Financial Hub</h2>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                        {activeCountry} Node 
                        {isSyncing && <Loader2 size={10} className="animate-spin text-indigo-400" />}
                    </p>
                </div>
             </div>
          </div>

          <div className="flex items-center gap-3">
             <button onClick={() => setIsHistoryOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-white text-slate-600 border border-slate-200 rounded-xl text-xs font-bold transition-all hover:bg-slate-50">
                <History size={16} /> History
             </button>
             <button onClick={() => setIsChatOpen(!isChatOpen)} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${isChatOpen ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white text-slate-600 border border-slate-200'}`}>
                <Bot size={16} /> Sidekick
             </button>
             <button onClick={() => setIsFinalizeOpen(true)} className="px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest bg-emerald-600 text-white hover:bg-emerald-700 shadow-xl transition-all flex items-center gap-2">
                <CheckCircle2 size={16} /> Confirm & Save
             </button>
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden">
          <div className="w-80 shrink-0 border-r border-slate-200 bg-white no-print">
            <QuoteEditor data={quote} activeCountry={activeCountry} onChange={setQuote} onConfirm={() => setIsFinalizeOpen(true)} crmContacts={crmContacts} />
          </div>
          <div className="flex-1 bg-slate-100 p-8 overflow-y-auto custom-scrollbar flex justify-center items-start">
             <div className="shadow-2xl bg-white origin-top scale-90 lg:scale-100 print-area">
                <QuotePreview data={quote} country={activeCountry} />
             </div>
          </div>
          {isChatOpen && <QuoteChatAssistant quote={quote} onUpdateQuote={setQuote} onClose={() => setIsChatOpen(false)} />}
        </div>
      </div>

      <QuoteFinalizeModal isOpen={isFinalizeOpen} onClose={() => setIsFinalizeOpen(false)} quote={quote} onSave={handleManualSave} onDownload={() => window.print()} onSendForApproval={async () => {}} />
      <QuoteManager isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} currentQuote={quote} onLoadQuote={setQuote} currentUser={currentUser} />
    </div>
  );
};

export default FinanceModule;